import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, LayoutDashboard, Search, Check, X } from 'lucide-react';
export function ThreeLayersSlide() {
  const [activeCard, setActiveCard] = useState<number | null>(null);
  const cards = [{
    id: 1,
    icon: Eye,
    title: 'What Customers See',
    subtitle: 'Visual Design Layer',
    description: 'The beautiful, responsive interface your customers interact with. Conversion-optimized design, mobile-first, fast load times.',
    tags: ['Mobile responsive', 'Accessibility', 'CTA optimization'],
    color: 'blue',
    gradient: 'from-blue-500/20 to-blue-600/5',
    number: '01'
  }, {
    id: 2,
    icon: LayoutDashboard,
    title: 'Business Dashboard',
    subtitle: 'Communication Layer',
    description: 'The backend your team uses to interact with leads, manage customers, track conversions, and nurture relationships.',
    tags: ['Lead management', 'Automated workflows', 'Communication hub'],
    color: 'teal',
    gradient: 'from-teal-500/20 to-teal-600/5',
    number: '02'
  }, {
    id: 3,
    icon: Search,
    title: 'What Google Sees',
    subtitle: 'SEO/Technical Layer',
    description: 'The semantic HTML, structured data, meta tags, and backend optimization that makes Google rank your site.',
    tags: ['Structured data', 'Meta optimization', 'AI-SEO compatible'],
    color: 'green',
    gradient: 'from-green-500/20 to-green-600/5',
    number: '03'
  }];
  const tableRows = [{
    name: 'Visual Design',
    godaddy: true,
    ghl: true,
    letlois: true
  }, {
    name: 'Communication Layer',
    godaddy: false,
    ghl: true,
    letlois: true
  }, {
    name: 'SEO/Technical Layer',
    godaddy: false,
    ghl: false,
    letlois: true
  }, {
    name: 'Human Strategy Team',
    godaddy: false,
    ghl: false,
    letlois: true
  }];
  return <section id="architecture" className="min-h-screen bg-dark py-24 px-4 flex flex-col justify-center" data-id="element-606">
      <div className="max-w-7xl mx-auto w-full" data-id="element-607">
        <div className="text-center mb-16" data-id="element-608">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-5xl font-bold mb-4" data-id="element-609">
            The 3 Layers of a Modern Website
          </motion.h2>
          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-xl text-text-muted" data-id="element-610">
            Most builders only optimize 1. We build all 3.
          </motion.p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20" data-id="element-611">
          {cards.map((card, index) => <motion.div key={card.id} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} onMouseEnter={() => setActiveCard(card.id)} onMouseLeave={() => setActiveCard(null)} className={`relative rounded-2xl p-8 border border-white/10 bg-gradient-to-br ${card.gradient} cursor-pointer transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:border-white/20 overflow-hidden`} data-id="element-612">
              <div className="absolute top-0 right-0 p-4 text-8xl font-bold text-white/5 select-none pointer-events-none leading-none" data-id="element-613">
                {card.number}
              </div>

              <div className={`p-4 rounded-xl bg-white/5 w-fit mb-6 text-${card.color}-400 relative z-10`} data-id="element-614">
                <card.icon size={32} data-id="element-615" />
              </div>

              <h3 className="text-2xl font-bold mb-2 relative z-10" data-id="element-616">
                {card.title}
              </h3>
              <p className="text-sm text-text-muted uppercase tracking-wider mb-4 font-semibold relative z-10" data-id="element-617">
                {card.subtitle}
              </p>

              <motion.div initial={{
            height: 'auto',
            opacity: 0.8
          }} animate={{
            opacity: activeCard === card.id ? 1 : 0.7
          }} className="relative z-10" data-id="element-618">
                <p className="text-text-dim mb-6 leading-relaxed" data-id="element-619">
                  {card.description}
                </p>

                <div className="flex flex-wrap gap-2" data-id="element-620">
                  {card.tags.map(tag => <span key={tag} className="text-xs px-2 py-1 rounded-md bg-white/5 text-white/80 border border-white/10" data-id="element-621">
                      {tag}
                    </span>)}
                </div>
              </motion.div>
            </motion.div>)}
        </div>

        {/* 3 Layers Image */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="mb-24 flex justify-center" data-id="element-622">
          <img src="https://cdn.magicpatterns.com/uploads/snEe2APy8351wNfWtSbDJ9/3_Layers_to_a_Website.png" alt="3 Layers of a Modern Website" className="w-full max-w-2xl h-auto drop-shadow-2xl" data-id="element-623" />
        </motion.div>

        {/* Comparison Table - 4 columns now */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.4
      }} className="bg-dark-card rounded-2xl border border-white/10 overflow-hidden max-w-5xl mx-auto" data-id="element-624">
          <div className="grid grid-cols-4 bg-white/5 p-4 font-bold text-sm md:text-base" data-id="element-625">
            <div className="pl-4" data-id="element-626">Layer</div>
            <div className="text-center text-text-muted" data-id="element-627">GoDaddy / Wix</div>
            <div className="text-center text-text-muted" data-id="element-628">GoHighLevel</div>
            <div className="text-center text-primary" data-id="element-629">LetLois</div>
          </div>

          <div className="divide-y divide-white/5" data-id="element-630">
            {tableRows.map((row, i) => <motion.div key={row.name} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.6 + i * 0.1
          }} className="grid grid-cols-4 p-4 items-center hover:bg-white/5 transition-colors" data-id="element-631">
                <div className="font-medium pl-4 text-sm md:text-base" data-id="element-632">
                  {row.name}
                </div>
                <div className="flex justify-center" data-id="element-633">
                  {row.godaddy ? <Check className="text-green-500" size={20} data-id="element-634" /> : <X className="text-red-500" size={20} data-id="element-635" />}
                </div>
                <div className="flex justify-center" data-id="element-636">
                  {row.ghl ? <Check className="text-green-500" size={20} data-id="element-637" /> : <X className="text-red-500" size={20} data-id="element-638" />}
                </div>
                <div className="flex justify-center" data-id="element-639">
                  {row.letlois ? <div className="bg-primary/20 p-1 rounded-full" data-id="element-640">
                      <Check className="text-primary" size={20} data-id="element-641" />
                    </div> : <X className="text-red-500" size={20} data-id="element-642" />}
                </div>
              </motion.div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}